import React from 'react';
import { Typography, Button, Box, Stack, LinearProgress, Paper } from '@mui/material';
import { CheckCircle, GitHub, Circle } from '@mui/icons-material';

export const DeployAnimation: React.FC = () => {
  const [activeStep, setActiveStep] = React.useState(0);

  React.useEffect(() => {
    const timer = setInterval(() => {
      setActiveStep((prev) => (prev + 1) % 4);
    }, 4000);
    return () => clearInterval(timer);
  }, []);

  const steps = [
    {
      title: 'Connect Repository',
      content: (
        <Stack spacing={3} alignItems="center">
          <GitHub sx={{ fontSize: 40, color: '#fff' }} />
          <Button 
            variant="contained" 
            startIcon={<GitHub />}
            sx={{ 
              bgcolor: '#24292e',
              '&:hover': { bgcolor: '#2f363d' },
              px: 3,
              py: 1,
              fontSize: '0.875rem'
            }}
          >
            Connect GitHub
          </Button>
        </Stack>
      )
    },
    {
      title: 'Select Provider',
      content: (
        <Stack spacing={2}>
          <Stack direction="row" spacing={1} justifyContent="center">
            {['AWS', 'GCP', 'Azure'].map((provider) => (
              <Paper
                key={provider}
                elevation={0}
                sx={{
                  p: 2,
                  width: '70px',
                  textAlign: 'center',
                  bgcolor: 'rgba(255,255,255,0.1)',
                  fontSize: '0.875rem'
                }}
              >
                {provider}
              </Paper>
            ))}
          </Stack>
        </Stack>
      )
    },
    {
      title: 'Analyzing',
      content: (
        <Stack spacing={2} sx={{ width: '100%' }}>
          <Box sx={{ width: '100%', position: 'relative' }}>
            <LinearProgress 
              variant="determinate" 
              value={100} 
              sx={{ 
                height: 6, 
                borderRadius: 3,
                '& .MuiLinearProgress-bar': {
                  transition: 'transform 3s linear'
                }
              }} 
            />
            <Typography 
              variant="caption" 
              sx={{ 
                mt: 1,
                display: 'block',
                textAlign: 'center',
                color: 'text.secondary'
              }}
            >
              Scanning dependencies...
            </Typography>
          </Box>
        </Stack>
      )
    },
    {
      title: 'Deployed',
      content: (
        <Stack spacing={2} alignItems="center">
          <CheckCircle sx={{ color: '#10B981', fontSize: 40 }} />
          <Typography variant="body2" sx={{ textAlign: 'center' }}>
            CI/CD Pipeline Configured
          </Typography>
        </Stack>
      )
    }
  ];

  return (
    <Box
      sx={{
        width: { xs: '100%', sm: '500px' },
        mx: 'auto',
        position: 'relative',
        bgcolor: 'background.paper', // Using theme color for adaptability
        borderRadius: 2,
        overflow: 'hidden',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
        border: '1px solid',
        borderColor: 'divider'
      }}
    >
      {/* Browser Window Frame */}
      <Box sx={{ 
        width: '100%',
        bgcolor: (theme) => theme.palette.mode === 'dark' ? '#000' : '#fff',
        p: 1,
        borderTopLeftRadius: 8,
        borderTopRightRadius: 8,
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        borderBottom: '1px solid',
        borderColor: 'divider'
      }}>
        <Stack direction="row" spacing={0.5}>
          <Circle sx={{ color: '#EF4444', fontSize: 10 }} />
          <Circle sx={{ color: '#F59E0B', fontSize: 10 }} />
          <Circle sx={{ color: '#10B981', fontSize: 10 }} />
        </Stack>
        <Box sx={{ 
          flex: 1,
          mx: 1,
          bgcolor: (theme) => theme.palette.mode === 'dark' ? '#1E1E1E' : '#F5F5F5',
          borderRadius: 1,
          py: 0.5,
          px: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
            deploy.qixbi.com
          </Typography>
        </Box>
      </Box>

      {/* Content Area */}
      <Box>
        {/* Progress Steps */}
        <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Stack direction="row" spacing={1} alignItems="center">
            {steps.map((step, index) => (
              <React.Fragment key={index}>
                <Typography 
                  variant="caption" 
                  sx={{ 
                    fontSize: '0.75rem',
                    color: index <= activeStep ? 'primary.main' : 'text.secondary',
                    transition: 'color 0.3s'
                  }}
                >
                  {step.title}
                </Typography>
                {index < steps.length - 1 && (
                  <Box 
                    sx={{ 
                      flex: 1,
                      height: 1,
                      bgcolor: index < activeStep ? 'primary.main' : 'divider',
                      transition: 'background-color 0.3s'
                    }} 
                  />
                )}
              </React.Fragment>
            ))}
          </Stack>
        </Box>

        {/* Animation Content Area */}
        <Box sx={{ 
          p: 3,
          minHeight: '200px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: (theme) => theme.palette.mode === 'dark' ? '#1A1A1A' : '#FFFFFF'
        }}>
          <Box
            sx={{
              opacity: 0,
              animation: 'fadeIn 0.5s forwards',
              '@keyframes fadeIn': {
                to: { opacity: 1 }
              }
            }}
          >
            {steps[activeStep].content}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default DeployAnimation;